import { useEffect, useRef, useState } from "react";
import { GrButton } from "@jnpmedi/grebob";
import { useMutation } from "@tanstack/react-query";
import { MvnMessage } from "@newLibs/utils";
import { useInterval } from "@newHooks/common/useInterval";
import { notify } from "@newHooks/context/snackbar/notify";
import { useDocumentSignBySignerTokenLazyQuery } from "@newHooks/query/documentSigner/documentSignBySignerTokenLazy.query";
import { IdentificationRequestQueries } from "./identification";
import { DOCS_DOCUMENT_CODE } from "@maven-msg/code/docs";
import { AUTH_USER_VERIFICATION_CODE_LANG } from "@maven-msg/lang/auth";
import { DOCS_DOCUMENT_SIGN_LANG } from "@maven-msg/lang/docs";
import { HttpRequest, RestError } from "@maven-rest/common/HttpRequest";
import { DocumentSignResource, GetDocumentSignBySignerTokenRequest } from "@maven-rest/docs/DocumentSign.dto";

// NOTE: maven-lib 에 있던 것이라 제거 과정에서 이쪽으로 옮겨둠. 적절한 submodule 로 이전 바랍니다.
interface BrowserWindowConfig {
  [key: string]: number | string;
  width: number;
  height: number;
  left: number;
  top: number;
  scrollbars: string;
  status: string;
  resizable: string;
}

interface IdentificationButtonByDanalProps {
  token: string;
  buttonId: string;
  callbackUrl: string;
  callbackFunction: (responseData: DocumentSignResource | null, encrypted: string | null) => void;
  className?: string;
}

function getIdentificationKeyToDanalNames() {
  const map = new Map<string, string>();

  map.set("tradeId", "TID");
  map.set("backUrl", "BackURL");
  map.set("bgColor", "BgColor");
  map.set("isCharSet", "IsCharSet");
  map.set("isCarrier", "IsCarrier");
  map.set("isDestinationAddress", "IsDstAddr");
  map.set("byButtonId", "ByButtonId");
  map.set("byOriginUrl", "ByOriginURL");

  return map;
}

function getWindowConfig(width: number, height: number) {
  const windowConfig: Array<string> = [];
  const config: BrowserWindowConfig = {
    width: width,
    height: height,
    left: (window.screen.width - width) / 2,
    top: (window.screen.height - height) / 2,
    scrollbars: "yes",
    status: "yes",
    resizable: "no",
  };

  Object.keys(config).forEach((key) => {
    windowConfig.push(`${key}=${config[key]}`);
  });
  return windowConfig.join(",");
}

function createIdentificationForm(queries: IdentificationRequestQueries, targetFrame: string) {
  const identificationKeyNames = getIdentificationKeyToDanalNames();
  const frm = document.createElement("form");

  for (const key in queries) {
    const input = document.createElement("input");

    input.type = "hidden";
    input.name = identificationKeyNames.get(key) || "";
    input.value = queries[key] || "";
    frm.appendChild(input);
  }

  frm.name = targetFrame;
  frm.method = "post";
  frm.action = "https://wauth.teledit.com/Danal/WebAuth/Web/Start.php";
  frm.target = targetFrame;

  document.body.appendChild(frm);

  return frm;
}

function openWindow(queries: IdentificationRequestQueries, width: number, height: number, onBeforeUnload: () => void) {
  const targetFrame = queries.byButtonId || "identificationForm";
  const frm = createIdentificationForm(queries, targetFrame);
  const identificationWindow = window.open("", targetFrame, getWindowConfig(width, height));

  if (identificationWindow) {
    identificationWindow.onbeforeunload = onBeforeUnload;
  }

  frm.submit();
  return identificationWindow;
}

export function IdentificationButtonByDanal({
  token,
  buttonId,
  callbackUrl,
  callbackFunction,
  className,
}: IdentificationButtonByDanalProps) {
  const [identificationWindow, setIdentificationWindow] = useState<Window | null>(null);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [queries, setQueries] = useState<IdentificationRequestQueries>();
  const encrypted = useRef<string>();

  const { mutate: mutateVerifyDanalMember, isLoading: isVerifyDanalMemberLoading } = useMutation({
    mutationFn: async () => {
      const resp = await HttpRequest.request(
        process.env.REACT_APP_SANTA_BE_URL || "",
        "POST",
        "/identification/prepare/danal",
        { callbackUrl: `${process.env.REACT_APP_SANTA_BE_URL}/identification-redirect/confirm/danal` },
        {}
      );
      return resp.data;
    },
    onSuccess: (responseData: any) => {
      setQueries({
        tradeId: responseData?.tradeId ?? "",
        isCharSet: "UTF-8",
        byButtonId: buttonId,
        byOriginUrl: callbackUrl,
      });
    },
    onError: (error: RestError) => {
      notify(MvnMessage("Request failed identity verification", { message: error.responseMessage }), "WARN");
    },
  });

  const { trigger: triggerDocumentSignBySignerToken, isInitialLoading: isDocumentSignBySignerTokenInitialLoading } =
    useDocumentSignBySignerTokenLazyQuery({
      onSuccess: (data) => {
        callbackFunction(data.documentSign, null);
      },
      onError: (error) => {
        const emailRegistration = error.responseCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_REQUIRED_EMAIL_REGISTRATION.code;
        const emailVerification = error.responseCode === DOCS_DOCUMENT_CODE.DOCS_DOCUMENT_SIGN_REQUIRED_EMAIL_VERIFICATION.code;

        if (emailVerification || emailRegistration) {
          callbackFunction(null, encrypted.current || null);
          return;
        }

        callbackFunction(null, null);
        notify(
          MvnMessage(AUTH_USER_VERIFICATION_CODE_LANG.USER_VERIFICATION_CODE_IDENTITY_VERIFY_FAIL, { message: error.responseMessage }),
          "WARN"
        );
      },
    });

  useEffect(() => {
    const messageEventFunction = (event: MessageEvent) => {
      const responseData = event?.data;

      if (
        responseData &&
        responseData.buttonId === buttonId &&
        "result" in responseData &&
        "returnCode" in responseData &&
        "returnMsg" in responseData &&
        "tradeId" in responseData
      ) {
        encrypted.current = responseData.encrypted;

        const req: GetDocumentSignBySignerTokenRequest = {
          token,
          personalIdentificationEncrypted: responseData.encrypted,
        };

        triggerDocumentSignBySignerToken(req);
      }
    };

    window.addEventListener("message", messageEventFunction);
    return () => {
      window.removeEventListener("message", messageEventFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const width = 420;
    const height = 700;
    const onBeforeUnload = () => {
      setDisabled(true);
    };

    if (queries) {
      const identificationWindow = openWindow(queries, width, height, onBeforeUnload);
      setIdentificationWindow(identificationWindow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  useInterval(
    () => {
      if (identificationWindow?.closed) {
        setIdentificationWindow(null);
        setDisabled(false);
        setQueries(undefined);
      }
    },
    250,
    !identificationWindow
  );

  return (
    <GrButton
      className={className}
      fullWidth
      disabled={disabled || isVerifyDanalMemberLoading || isDocumentSignBySignerTokenInitialLoading}
      onClick={() => mutateVerifyDanalMember()}
    >
      {MvnMessage(DOCS_DOCUMENT_SIGN_LANG.DOCUMENT_SIGN_PROCEED_VERIFY)}
    </GrButton>
  );
}
